import React, { useState, useEffect } from 'react';
import { VERSION_STRING } from '../version';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaFacebookF, FaGoogle } from 'react-icons/fa';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from '../firebaseConfig';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import logoXCoin from '../assets/logo_plateado.png';
import background from '../assets/backhome.png';

const LoginPage = ({ isModalProp = false, onCloseProp }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isForgotModalOpen, setIsForgotModalOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // --- 1. DETECCIÓN ROBUSTA DE MODO MODAL ---
  // Verificamos si la prop es true O si el parámetro 'modal' existe en la URL
  const isModal = isModalProp || searchParams.get('modal') === 'true';

  // --- 2. CONFIGURACIÓN DE URL BASE (Evita 'undefined') ---
  // Si la variable de entorno falla, usa el origen actual (ej: dashboard.xcoin.cl)
  const DASHBOARD_URL = import.meta.env.VITE_DASHBOARD_APP_URL || window.location.origin;
  const MARKETING_URL = import.meta.env.VITE_MARKETING_APP_URL || 'https://xcoin.cl';

  // --- 3. ESTILOS DEPURADOS PARA EL MODAL ---
  const wrapperStyle = isModal ? {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: '#fff', // Fondo blanco limpio en el iframe
    padding: '20px'
  } : {};

  const formSideStyle = isModal ? {
    width: '100%',
    maxWidth: '400px', // Ancho máximo para que no se estire feo
    padding: '0',
    boxShadow: 'none' // Quitamos sombras extras si es modal
  } : {};

  const backgroundSideStyle = isModal ? { display: 'none' } : { backgroundImage: `url(${background})` };

  // --- 4. LÓGICA DE REDIRECCIÓN (SALIR DEL IFRAME) ---
  const handleLoginSuccess = () => {
    setSuccessMessage('¡Inicio de sesión exitoso!');

    // Verificamos si hay un target de redirección
    const redirectTarget = searchParams.get('redirect');

    if (redirectTarget) {
      if (redirectTarget === 'trading') {
        window.location.href = `${DASHBOARD_URL}/trading-automatizado`;
        return;
      }
      if (redirectTarget === 'comprar') {
        window.location.href = `${DASHBOARD_URL}/mercado/comprar-vender`;
        return;
      }
    }

    // Caso A: Estamos en un Iframe (Landing Page)
    if (searchParams.get('modal') === 'true') {
      // window.top.location cambia la URL de la pestaña principal del navegador
      if (redirectTarget === 'trading') {
        window.top.location.href = `${DASHBOARD_URL}/trading-automatizado`;
      } else {
        window.top.location.href = `${DASHBOARD_URL}`;
      }
    }
    // Caso B: Modal Interno (Dashboard)
    else if (isModalProp) {
      // Recarga la página actual o cierra el modal
      window.location.href = DASHBOARD_URL;
    }
    // Caso C: Login Normal
    else {
      // Si no hubo redirect específico, vamos al home
      setTimeout(() => navigate('/'), 1500);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setCargando(true);
    setError(null);
    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithEmailAndPassword(auth, email, password);
      handleLoginSuccess();
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Correo o contraseña incorrectos.');
      } else {
        setError('Ocurrió un error al iniciar sesión.');
      }
      setCargando(false);
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    setCargando(true);
    try {
      await setPersistence(auth, browserLocalPersistence);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const gestionarPerfil = httpsCallable(functions, 'gestionarPerfilDeUsuario');
      await gestionarPerfil({ nombre: user.displayName, fotoURL: user.photoURL });
      handleLoginSuccess();
    } catch (err) {
      setError('No se pudo iniciar sesión con Google.');
      setCargando(false);
    }
  };

  const handleFacebookLogin = async () => {
    const provider = new FacebookAuthProvider();
    setCargando(true);
    try {
      await setPersistence(auth, browserLocalPersistence);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const gestionarPerfil = httpsCallable(functions, 'gestionarPerfilDeUsuario');
      await gestionarPerfil({ nombre: user.displayName, fotoURL: user.photoURL });
      handleLoginSuccess();
    } catch (err) {
      setError('No se pudo iniciar sesión con Facebook.');
      setCargando(false);
    }
  };

  return (
    <>
      {/* Aplicamos estilo especial si es modal */}
      <div className={`login-page-wrapper ${isModal ? 'modal-mode' : ''}`} style={isModal ? wrapperStyle : {}}>

        {/* Botón cerrar solo si es modal interno (Prop) */}
        {isModalProp && onCloseProp && (
          <button
            onClick={onCloseProp}
            style={{ position: 'absolute', top: '10px', right: '15px', background: 'transparent', border: 'none', fontSize: '2rem', cursor: 'pointer', zIndex: 100 }}
          >
            &times;
          </button>
        )}

        <div className="form-side" style={formSideStyle}>
          <div className="form-content" style={{ width: '100%' }}>

            {/* Ocultamos botón Volver si es Modal */}
            {!isModal && (
              <div style={{ marginBottom: '1rem' }}>
                <a href={MARKETING_URL} className="btn btn-back-home">&larr; Volver al Inicio</a>
              </div>
            )}

            <div className="form-header">
              <img src={logoXCoin} alt="XCoin Logo" className="logo" style={{ width: isModal ? '200px' : '300px', height: 'auto' }} />
            </div>

            <div className="form-body">
              <h2>{isModal ? 'Bienvenido' : 'Información Personal'}</h2>
              <p>Ingresa con tu correo y contraseña.</p>

              <form onSubmit={handleLogin}>
                <div className="input-group">
                  <input
                    id="login-email"
                    name="email"
                    type="email"
                    placeholder="ejemplo@gmail.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="input-group">
                  <input
                    id="login-password"
                    name="password"
                    type="password"
                    placeholder="******"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>

                {error && <div className="alert alert-danger" style={{ padding: '10px', backgroundColor: '#f8d7da', color: '#721c24' }}>{error}</div>}
                {successMessage && <div className="alert alert-success" style={{ padding: '10px', backgroundColor: '#d4edda', color: '#155724' }}>{successMessage}</div>}

                <div className="form-actions login-modal-actions">
                  <button type="submit" className="btn btn-primary" disabled={cargando}>{cargando ? 'Ingresando...' : 'Ingresar'}</button>
                  <button type="button" className="btn btn-primary" onClick={(e) => { e.preventDefault(); setIsForgotModalOpen(true); }}>¿Olvidaste tu Contraseña?</button>

                  {/* El botón de registro en el modal debe llevar al registro */}
                  {isModal && (
                    <a href={`${DASHBOARD_URL}/register?modal=true`} className="btn btn-primary" style={{ textAlign: 'center', textDecoration: 'none', lineHeight: 'normal' }}>
                      Regístrate
                    </a>
                  )}
                </div>
              </form>

              {/* Redes sociales siempre visibles */}
              <div className="social-login">
                <h4>Iniciar sesión con</h4>
                <div className="social-icons">
                  <a href="#" onClick={(e) => { e.preventDefault(); handleGoogleLogin(); }} title="Iniciar sesión con Google"><FaGoogle /></a>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleFacebookLogin(); }} title="Iniciar sesión con Facebook"><FaFacebookF /></a>
                </div>
              </div>
            </div>

            {!isModal && <footer className="footer">Copyright © XCoin {new Date().getFullYear()}. Todos los derechos reservados. <span style={{ opacity: 0.7, fontSize: '0.9em', marginLeft: '10px' }}>{VERSION_STRING}</span></footer>}
          </div>
        </div>
        <div className="background-side" style={backgroundSideStyle}></div>
      </div>

      {isForgotModalOpen && (
        <ForgotPasswordModal onClose={() => setIsForgotModalOpen(false)} onSent={() => setIsForgotModalOpen(false)} />
      )}
    </>
  );
};

export default LoginPage;
